import { HELPER_LABELS, type Helpers } from '../helpers.js';

export interface HelperPanelProps {
  readonly helpers: Helpers;
  readonly onChange: (helpers: Helpers) => void;
  readonly showTerritory: boolean;
  readonly onToggleTerritory: (on: boolean) => void;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * The training wheels, and the switch for taking them off.
 *
 * Every one of these answers something a stronger player reads at a glance, so
 * leaving them on forever would be the failure case. They are laid out as plain
 * checkboxes with a line of explanation each, because a learner should be able
 * to tell what a helper is for before turning it on rather than after.
 *
 * The whole panel folds away, since it is a thing you set and then stop looking
 * at. Collapsed it still reports how many are on, so nothing switched on can be
 * forgotten about behind a closed lid.
 */
export function HelperPanel({
  helpers,
  onChange,
  showTerritory,
  onToggleTerritory,
  open,
  onOpenChange,
}: HelperPanelProps) {
  const active = Object.values(helpers).filter(Boolean).length + (showTerritory ? 1 : 0);

  return (
    <details
      className="helpers"
      open={open}
      onToggle={(event) => onOpenChange((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>
        <span className="helpers-title">Helpers</span>
        <span className="muted small">{active} on</span>
      </summary>

      <p className="muted small">
        Things a stronger player would see without being told. Turn them off as you stop needing
        them — that is what progress looks like here.
      </p>

      <ul className="helper-list">
        {HELPER_LABELS.map((entry) => (
          <li key={entry.key}>
            <label>
              <input
                type="checkbox"
                checked={helpers[entry.key]}
                onChange={(event) => onChange({ ...helpers, [entry.key]: event.target.checked })}
              />
              <span>
                <strong>{entry.label}</strong>
                <span className="muted small">{entry.hint}</span>
              </span>
            </label>
          </li>
        ))}

        {/* Territory lives here too, so every board overlay is in one place. */}
        <li>
          <label>
            <input
              type="checkbox"
              checked={showTerritory}
              onChange={(event) => onToggleTerritory(event.target.checked)}
            />
            <span>
              <strong>Territory</strong>
              <span className="muted small">
                Shades who currently surrounds what. An estimate, and it assumes every stone is
                alive.
              </span>
            </span>
          </label>
        </li>
      </ul>
    </details>
  );
}
